import { memo, useMemo } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Link } from "react-router-dom"

import {
  bookmarkColor,
  folder,
  folders,
  othersSnippets,
  pinnedSnippets,
  search,
  setBookmarkColorFilter,
  setFolderFilter,
  setSearchFilter,
} from "rtk-slice"

import { handleNoneEmptyFolders } from "utils/folders"
import { isMobile } from "utils/mediaQuery"

import {
  Badge,
  Chip,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material"

import {
  BookmarkShape,
  FolderAltSolid,
  FolderDuotone,
  HomeColors,
  Search,
} from "ui-icons"

const Breadcrumbs = () => {
  const dispatch = useDispatch()
  const {
    palette: {
      primary: { main },
      error: { main: errorMain },
    },
  } = useTheme()
  const folderState = useSelector(folder)
  const foldersState = useSelector(folders)
  const bookmarkColorState = useSelector(bookmarkColor)

  const searchState = useSelector(search)

  const pinnedSnippetsState = useSelector(pinnedSnippets)

  const othersSnippetsState = useSelector(othersSnippets)

  const resetFolderFilter = () => {
    dispatch(setFolderFilter(""))
  }

  const clearSearch = () => {
    dispatch(setSearchFilter(""))
  }

  const resetColorFilterState = () => {
    dispatch(setBookmarkColorFilter(""))
  }

  const noneEmptyFoldersLength = useMemo(
    () =>
      handleNoneEmptyFolders(
        [...othersSnippetsState, ...pinnedSnippetsState],
        foldersState
      ).length,
    [foldersState, othersSnippetsState, pinnedSnippetsState]
  )

  return (
    <Stack
      sx={{
        flexDirection: "row",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
      }}
      columnGap="0.5em"
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        justifySelf="flex-start"
        alignItems="center"
        spacing={1}
      >
        <Tooltip
          title={
            foldersState.length
              ? "Desktop"
              : "You have no folders yet!"
          }
        >
          <Badge
            badgeContent={foldersState.length}
            color="error"
            overlap="circular"
            max={99}
            showZero
            sx={{
              "& .MuiBadge-badge": {
                top: "-0.5em !important",
              },
            }}
          >
            <Link
              to={searchState ? `/?keyword=${searchState}` : "/"}
              style={{ textDecoration: "none" }}
            >
              <Chip
                icon={<HomeColors width="20" height="20" />}
                label={
                  isMobile() ? null : (
                    <Typography variant="subtitle2" component="span">
                      Desktop
                    </Typography>
                  )
                }
                onClick={resetFolderFilter}
                sx={{
                  px: "0.5em",
                  borderRadius: "0.25em",
                  "&:hover .MuiChip-label": {
                    textDecoration: "underline dotted !important",
                  },
                  "& .MuiChip-label": {
                    px: isMobile() ? "0em" : "0.5em",
                    alignSelf: "flex-end",
                    py: "0.35em",
                  },
                }}
              />
            </Link>
          </Badge>
        </Tooltip>
      </Stack>
      <Typography
        variant="h6"
        color="inherit"
        component="span"
        alignSelf="flex-end"
        py="0.25em"
      >
        /
      </Typography>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="center"
        px="0em"
      >
        <Tooltip title={folderState?.length > 10 ? folderState : ""}>
          <Chip
            icon={
              folderState ? (
                <FolderAltSolid
                  fill={errorMain}
                  width="20"
                  height="20"
                />
              ) : (
                <FolderDuotone
                  width="20"
                  height="20"
                  fill={main}
                  body={noneEmptyFoldersLength ? "#444" : main}
                />
              )
            }
            label={
              <Typography variant="subtitle2" component="span">
                {folderState
                  ? folderState.length > (isMobile() ? 8 : 30)
                    ? `${folderState.slice(
                        0,
                        isMobile() ? 9 : 31
                      )}...`
                    : folderState
                  : folderState === null
                  ? "Root"
                  : "All Snippets"}
              </Typography>
            }
            variant="outlined"
            color="primary"
            onClick={resetFolderFilter}
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              px: "0em",
              height: "2.7em",
              border: "none",
              "&:hover": {
                bgcolor: "transparent !important",
              },
              "& .MuiChip-label": {
                justifySelf: "center",
                pl: isMobile() ? "0.25em" : "0.5em",
                pr: "0em",
                py: "0.35em",
                display: "flex",
                alignSelf: "flex-end",
                "&:hover": {
                  textDecoration: "underline dotted !important",
                },
              },
            }}
          />
          {/* </Badge> */}
        </Tooltip>
      </Stack>
      {searchState ? (
        <Typography
          variant="h6"
          color="inherit"
          component="span"
          alignSelf="flex-end"
          py="0.25em"
        >
          /
        </Typography>
      ) : null}
      {searchState ? (
        <Stack
          direction="row"
          spacing="0.5em"
          alignItems="center"
          justifyContent="center"
          margin="0em !important"
        >
          <Search width="20" height="20" fill={errorMain} />

          <Tooltip title={searchState}>
            <Chip
              color="primary"
              label={
                <Typography variant="subtitle2" component="span">
                  {searchState.length > (isMobile() ? 8 : 30)
                    ? `${searchState.slice(
                        0,
                        isMobile() ? 9 : 31
                      )}...`
                    : searchState}
                </Typography>
              }
              variant="outlined"
              onClick={clearSearch}
              sx={{
                height: "2.7em",
                border: "none",
                "&:hover": {
                  bgcolor: "transparent !important",
                },
                "& .MuiChip-label": {
                  px: "0em !important",
                  py: "0.35em",
                  alignSelf: "flex-end",
                  "&:hover": {
                    textDecoration: "underline dotted !important",
                  },
                },
              }}
            />
          </Tooltip>
        </Stack>
      ) : null}
      {bookmarkColorState ? (
        <Typography
          variant="h6"
          color="inherit"
          component="span"
          alignSelf="flex-end"
          py="0.25em"
        >
          /
        </Typography>
      ) : null}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="center"
        margin="0em !important"
        sx={{ cursor: "pointer" }}
        onClick={resetColorFilterState}
      >
        {bookmarkColorState ? (
          <BookmarkShape
            width="24"
            height="24"
            fill={bookmarkColorState}
          />
        ) : null}
      </Stack>
    </Stack>
  )
}

export default memo(Breadcrumbs)
